import { useId } from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/classNames.ts';

type ConsentProps = {
  setConsent: (checked: boolean) => void;
  userConsent: boolean;
  invalid?: boolean;
  disabled?: boolean;
};

export function ConsentComponent({ setConsent, userConsent, invalid = false, disabled = false }: ConsentProps) {
  const { t } = useTranslation();
  const errorId = useId();

  return (
    <div className="w-full">
      <label
        className={cn(
          'flex cursor-pointer items-start gap-4 text-left font-sans text-lg leading-relaxed',
          invalid ? 'text-error' : 'text-[var(--cream)]'
        )}
      >
        <span className="relative mt-1 flex h-6 w-6 shrink-0">
          <input
            type="checkbox"
            name="healthConsent"
            checked={userConsent}
            aria-invalid={invalid}
            aria-describedby={invalid ? errorId : undefined}
            disabled={disabled}
            onChange={(event) => setConsent(event.target.checked)}
            className="peer h-full w-full cursor-pointer appearance-none rounded-md border border-[var(--gold)] checked:bg-[var(--gold)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--gold)]"
          />
          <CheckIcon
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 m-auto h-5 w-5 text-[var(--ink)] opacity-0 peer-checked:opacity-100"
          />
        </span>
        <span>
          {t('consent.healthInformation', {
            defaultValue:
              'I agree to Journi using this health information to plan my trips, including sending it to our AI provider. You can withdraw this any time.',
          })}{' '}
          <a
            href="https://www.nextjourni.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className={cn('underline underline-offset-2', invalid ? 'text-error' : 'text-[var(--gold)]')}
          >
            {t('consent.privacyPolicy', { defaultValue: 'Read the Privacy Policy' })}
          </a>
        </span>
      </label>
      {invalid && (
        <span id={errorId} role="alert" className="mt-2 block text-left text-sm text-error">
          {t('consent.required')}
        </span>
      )}
    </div>
  );
}
